import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Drawer } from '@/components';
import { OfferEntryTable } from '@/features/offers/types';
import { useOfferDetails } from '@/features/offers/hooks';
import { useBelongsToMyCompany } from '@/features/company/hooks';
import { ActionOfferModals } from '@/features/offers/components/modals/action-offer-modals';
import { useRegisterGAPageView } from '@/hooks';
import { OfferDrawerFooter } from './footer';
import { OfferDrawerContent } from './content';

export type ManageOfferDrawerProps = {
  readonly offer: OfferEntryTable | null;
  readonly onClose: () => void;
};

export function ManageOfferDrawer({ offer, onClose }: ManageOfferDrawerProps) {

  const { t } = useTranslation(['offers']);

  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const isOpen = !!offer;
  const offerData = useOfferDetails(offer?.id);

  const offerBelongsToMyCompany = useBelongsToMyCompany(offerData?.company?.id);
  const opportunityBelongsToMyCompany = useBelongsToMyCompany(offerData?.opportunity?.company);

  const canAct = !offerBelongsToMyCompany && opportunityBelongsToMyCompany && offerData?.status.name === 'pending';
  useRegisterGAPageView(offerData?.opportunity?.name ? `Offer: ${offerData.opportunity.name}` : null);

  if (!offerData) return null;

  const footer: ReactNode = (
    <OfferDrawerFooter
      canAct={canAct}
      onAccept={() => setIsAcceptModalOpen(true)}
      onDecline={() => setIsDeclineModalOpen(true)}
      onClose={onClose}
    />
  );

  return (
    <>
      <Drawer
        title={t('New Expression of Interest Received')}
        open={isOpen}
        onClose={onClose}
        footer={footer}
      >
        <OfferDrawerContent offer={offerData} />
      </Drawer>

      {offerData?.opportunity && (
        <ActionOfferModals
          offer={offerData.id}
          opportunity={offerData.opportunity}
          isAcceptModalOpen={isAcceptModalOpen}
          isDeclineModalOpen={isDeclineModalOpen}
          setIsAcceptModalOpen={setIsAcceptModalOpen}
          setIsDeclineModalOpen={setIsDeclineModalOpen}
        />
      )}
    </>
  );
}
